import React, { useRef, useEffect, useState } from 'react';
import { socket } from './Socket';
import styles from './dwn-style.module.css';

const VideoPlayer = ({ uid, meeting_id, turnKey, role }) => {
  const peerConnectionRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunks = useRef([]);
  const iceServersRef = useRef([]);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);


  // ------------------------
  // Utility Functions
  // ------------------------

  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/mpeg'
    ];
  
    return types.find(t => window.MediaRecorder?.isTypeSupported(t)) || '';
  };
  
  const stopAllLocalMedia = () => {
    const localStream = localVideoRef.current?.srcObject;
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      localVideoRef.current.srcObject = null;
    }
  };

  const replaceTrack = (newTrack) => {
    const sender = peerConnectionRef.current
      .getSenders()
      .find(sender => sender.track.kind === newTrack.kind);
    if (sender) sender.replaceTrack(newTrack);
  };

  const getLocalCameraStream = async () => {
    return await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: { width: { ideal: 640 }, height: { ideal: 480 } }
    });
  };

  const getLocalScreenStream = async () => {
    return await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: 'always' },
      audio: true
    });
  };

  const uploadAudioWithRetry = (formData, retries = 3) => {
    return new Promise((resolve, reject) => {
      const attempt = (count) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", "https://app.digitalwellnessnurse.com/api/portal/upload/audio");
  
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percent);
          }
        };
  
        xhr.onload = () => {
          if (xhr.status === 200) resolve();
          else retry(count);
        };
  
        xhr.onerror = () => retry(count);
  
        const retry = (count) => {
          if (count > 0) {
            attempt(count - 1);
          } else {
            reject(new Error("Upload failed"));
          }
        };
  
        xhr.send(formData);
      };
  
      attempt(retries);
    });
  };
  

  const stopMeetingAndUploadAudio = async () => {
    setUploading(true);
    setUploadError(null);
  
    // CASE 1: No recorder ever started
    if (!mediaRecorderRef.current) {
      setUploadError("Recording was not started. No audio to upload.");
      console.warn("Recording was not started. No audio to upload.");
      stopAllLocalMedia();
      setUploading(false);
      return;
    }
    
  
    // CASE 2: Recorder exists but already inactive
    if (mediaRecorderRef.current.state === "inactive") {
      setUploadError("Recording was not started. No audio to upload.");
      console.warn("MediaRecorder inactive — skipping upload");
      stopAllLocalMedia();
      setUploading(false);
      //window.location.href = "/";
      return;
    }
  
    // CASE 3: Recorder is active → stop and upload
    await new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        try {
          if (recordedChunks.current.length > 0) {
            const audioBlob = new Blob(recordedChunks.current, { type: "audio/webm" });
            const formData = new FormData();
            formData.append("audio", audioBlob, `${Date.now()}_${meeting_id}.webm`);
  
              await uploadAudioWithRetry(formData);

              alert(
                "Meeting recording uploaded successfully.\n\nYou may now safely close this window."
              );
          }
        } catch (err) {
          setUploadError("Audio upload failed.");
        }
        resolve();
      };
  
      mediaRecorderRef.current.stop();
    });
  
    recordedChunks.current = [];
    stopAllLocalMedia();
    setUploading(false);
    console.log("File uploaded")
    //window.location.href = "/";
  };
  
  
  

  // ------------------------
  // WebRTC Handlers
  // ------------------------
  const handleReceiveOffer = async (offer) => {
    const pc = peerConnectionRef.current;
    if (!pc) return;

    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socket.emit('answer', { meeting_id, uid, answer: pc.localDescription });
  };

  const handleReceiveAnswer = async (answer) => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
    await pc.setRemoteDescription(new RTCSessionDescription(answer));
  };

  const handleNewICECandidate = async (candidate) => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
    await pc.addIceCandidate(new RTCIceCandidate(candidate));
  };

  const handleOnTrack = (event) => {
    remoteVideoRef.current.srcObject = event.streams[0];

    if (role === 'Provider') {
      console.log("Provider !!!")
      const localStream = localVideoRef.current.srcObject;
      const remoteStream = remoteVideoRef.current.srcObject;
      if (!localStream || !remoteStream) return;

      const combinedAudio = new MediaStream([
        ...localStream.getAudioTracks(),
        ...remoteStream.getAudioTracks()
      ]);

      const mimeType = getSupportedMimeType();

      if (!mimeType) {
        console.error("No supported MediaRecorder mimeType");
        return;
      }

      mediaRecorderRef.current = new MediaRecorder(combinedAudio, { mimeType });


      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunks.current.push(e.data);
      };

      mediaRecorderRef.current.start();
      console.log("MediaRecorder started:", mimeType);

    }
  };

  // ------------------------
  // Socket & PeerConnection Setup
  // ------------------------
  useEffect(() => {
    const setupPeerConnection = async () => {
      // Get TURN credentials
      const response = await fetch(`https://digitalwellnessnurse.metered.live/api/v1/turn/credentials?apiKey=${turnKey}`);
      iceServersRef.current = await response.json();

      // Get local camera stream
      const localStream = await getLocalCameraStream();
      localVideoRef.current.srcObject = localStream;

      // Initialize PeerConnection
      const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
      peerConnectionRef.current = pc;

      pc.onicecandidate = (e) => {
        if (e.candidate) socket.emit('candidate', { meeting_id, uid, candidate: e.candidate });
      };
      pc.ontrack = handleOnTrack;

      // Add local tracks to PeerConnection
      localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

      // Connect socket
      socket.connect();
      socket.emit('uid', uid);
      socket.emit('join-lobby', { uid, meeting_id });
    };

    setupPeerConnection();

    return () => {
      stopAllLocalMedia();
      peerConnectionRef.current?.close();
      peerConnectionRef.current = null;
      socket.disconnect();
    };
  }, [turnKey]);

  // ------------------------
  // Socket Event Listeners
  // ------------------------
  useEffect(() => {
    socket.on('offer', (data) => handleReceiveOffer(data.offer));
    socket.on('answer', (data) => handleReceiveAnswer(data.answer));
    socket.on('candidate', (data) => handleNewICECandidate(data.candidate));
    socket.on('all-present', async () => {
      const pc = peerConnectionRef.current;
      if (!pc) return;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('offer', { meeting_id, uid, offer });
    });

    const handleLeave = async () => {
      await stopMeetingAndUploadAudio();
      peerConnectionRef.current?.close();
      socket.disconnect();
    };


    window.addEventListener('leave-meeting', handleLeave);
    window.addEventListener('end-meeting', handleLeave);

    return () => {
      socket.off('offer');
      socket.off('answer');
      socket.off('candidate');
      socket.off('all-present');
      window.removeEventListener('leave-meeting', handleLeave);
      window.removeEventListener('end-meeting', handleLeave);
    };
  }, []);

  // ------------------------
  // Screen Share
  // ------------------------
  useEffect(() => {
    const handleShareScreen = async () => {
      const screenStream = await getLocalScreenStream();
      const screenTrack = screenStream.getVideoTracks()[0];
      if (screenTrack) replaceTrack(screenTrack);
    };
    const handleStopShare = async () => {
      const cameraStream = await getLocalCameraStream();
      const cameraTrack = cameraStream.getVideoTracks()[0];
      if (cameraTrack) replaceTrack(cameraTrack);
    };

    window.addEventListener('share-screen', handleShareScreen);
    window.addEventListener('stop-sharing', handleStopShare);

    return () => {
      window.removeEventListener('share-screen', handleShareScreen);
      window.removeEventListener('stop-sharing', handleStopShare);
    };
  }, []);

  // ------------------------
  // Mic & Video Toggle
  // ------------------------
  useEffect(() => {
    const toggleMic = () => {
      const track = localVideoRef.current?.srcObject?.getAudioTracks()[0];
      if (track) track.enabled = !track.enabled;
    };
    const toggleVideo = () => {
      const track = localVideoRef.current?.srcObject?.getVideoTracks()[0];
      if (track) track.enabled = !track.enabled;
    };
    window.addEventListener('toggle-mic', toggleMic);
    window.addEventListener('toggle-video', toggleVideo);

    return () => {
      window.removeEventListener('toggle-mic', toggleMic);
      window.removeEventListener('toggle-video', toggleVideo);
    };
  }, []);

  // ------------------------
  // PiP
  // ------------------------
  const togglePiP = async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (remoteVideoRef.current) {
        if (remoteVideoRef.current.paused) await remoteVideoRef.current.play();
        await remoteVideoRef.current.requestPictureInPicture();
      }
    } catch (err) {
      console.error('PiP error:', err);
    }
  };

  useEffect(() => {
    const handlePiP = () => togglePiP();
    window.addEventListener('PiP', handlePiP);
    return () => window.removeEventListener('PiP', handlePiP);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading) {
        e.preventDefault();
        e.returnValue =
          "Meeting audio is still uploading. Leaving now may interrupt the upload.";
      }
    };
  
    window.addEventListener("beforeunload", handleBeforeUnload);
  
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [uploading]);

return (
  <div className={styles.conference}>

    {/* Upload overlay */}
    {uploading && (
      <div className={styles.uploadOverlay}>
        <h3>Please do not close this browser window</h3>

        <p>
          The meeting recording is being uploaded.
          Closing this page may interrupt the upload.
        </p>

        <progress value={uploadProgress} max="100" />

        <p>{uploadProgress}% Complete</p>
      </div>
    )}
    {/* Upload error */}
    {uploadError && (
      <div className={styles.uploadError}>
        <p>{uploadError}</p>
      </div>
    )}

    {/* Videos */}
    <video
      ref={remoteVideoRef}
      className={styles.remote}
      playsInline
      autoPlay
      controls={false}
    />

    <video
      ref={localVideoRef}
      className={styles.local}
      playsInline
      autoPlay
      muted
      controls={false}
    />
  </div>
);

};

export default VideoPlayer;
